import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";

type MerchantDetailData = Record<string, any>;

type FormState = {
  company_name: string;
  company_name_kana: string;
  industry: string;
  industry_other: string;
  entity_type: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  notes: string;
};


const INDUSTRY_OPTIONS = ["美容医療", "エステティック", "BtoB", "異業種", "その他"] as const;


export default function MerchantDetail() {
  const { merchantId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<MerchantDetailData | null>(null);
  const [error, setError] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [uiError, setUiError] = useState("");
  const [uiSuccess, setUiSuccess] = useState("");

  const [form, setForm] = useState<FormState>({
    company_name: "",
    company_name_kana: "",
    industry: "",
    industry_other: "",
    entity_type: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
  });

  const apiBase = useMemo(() => "/api", []);

  const fillFormFromData = (d: MerchantDetailData) => {
    setForm({
      company_name: d.company_name || "",
      company_name_kana: d.company_name_kana || "",
      industry: d.industry || "",
      industry_other: d.industry_other || "",
      entity_type: d.entity_type || "",
      contact_name: d.contact_name || "",
      contact_email: d.contact_email || "",
      contact_phone: d.contact_phone || "",
      notes: d.notes || "",
    });
  };

  const fetchDetail = async () => {
    if (!merchantId) {
      setError("加盟店IDが取得できませんでした。");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${apiBase}/staff/merchants/${merchantId}`);
      if (!res.ok) {
        setError("加盟店詳細の取得に失敗しました。");
        setLoading(false);
        return;
      }
      const json = await res.json();
      setData(json);
      fillFormFromData(json);
      setLoading(false);
    } catch {
      setError("加盟店詳細の取得に失敗しました。バックエンドが起動しているか確認してください。");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const onStartEdit = () => {
    if (!data) return;
    setUiError("");
    setUiSuccess("");
    fillFormFromData(data);
    setIsEditing(true);
  };

  const onCancelEdit = () => {
    if (!data) return;
    setUiError("");
    setUiSuccess("");
    fillFormFromData(data);
    setIsEditing(false);
  };

  const validate = (): string => {
    if (!form.company_name.trim()) return "会社名は必須です。";
    if (!form.industry.trim()) return "業種は必須です。";
        if ((form.industry === "異業種" || form.industry === "その他") && !form.industry_other.trim())
      return "業種が「異業種」または「その他」の場合、業種（自由入力）は必須です。";
    
    if (!form.contact_name.trim()) return "担当者名は必須です。";
    if (!form.contact_email.trim()) return "担当者メールは必須です。";
    return "";
  };

  const onSave = async () => {
    if (!merchantId) return;
    setUiError("");
    setUiSuccess("");

    const msg = validate();
    if (msg) {
      setUiError(msg);
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/staff/merchants/${merchantId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
  ...form,
  merchantContacts: (data?.merchantContacts || []).filter((x: any) => x.name || x.email),
  jmcAssignees: (data?.jmcAssignees || []).filter((x: any) => x.name || x.email),
}),
      });

      if (!res.ok) {
        const t = await safeReadError(res);
        setUiError(t || "更新に失敗しました。");
        setSaving(false);
        return;
      }

      setUiSuccess("更新しました。");
      setIsEditing(false);
      await fetchDetail();
      setSaving(false);
    } catch {
      setUiError("更新に失敗しました。バックエンドが起動しているか確認してください。");
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!merchantId) return;
    setUiError("");
    setUiSuccess("");

    const ok = window.confirm("本当に削除しますか？この操作は取り消せません。");
    if (!ok) return;

    setDeleting(true);
    try {
      const res = await fetch(`${apiBase}/staff/merchants/${merchantId}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const t = await safeReadError(res);
        setUiError(t || "削除に失敗しました。");
        setDeleting(false);
        return;
      }

      navigate("/staff/merchants");
    } catch {
      setUiError("削除に失敗しました。バックエンドが起動しているか確認してください。");
      setDeleting(false);
    }
  };

  return (
    <StaffLayout title="加盟店詳細" active="manageMerchants">
      <div style={{ fontWeight: 800, marginBottom: 12 }}>加盟店詳細</div>

      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}
      {error && <div style={{ color: "#b91c1c", fontWeight: 800 }}>{error}</div>}

      {!loading && !error && (
        <>
          {uiError && <InlineMsg kind="error">{uiError}</InlineMsg>}
          {uiSuccess && <InlineMsg kind="success">{uiSuccess}</InlineMsg>}

          {data && (
            <div style={card}>
              <div style={accountBox}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>アカウント情報</div>
                <div style={{ fontSize: 14, lineHeight: 1.9, color: "#0f172a" }}>
                  <div>加盟店ID：{data.merchant_id || "-"}</div>
                  <div>
                    仮パスワード：
                    {data.temp_password ||
                      data.initial_password ||
                      data.merchant_password ||
                      data.password ||
                      "（DBに保存されていません）"}
                  </div>
                </div>
              </div>

              <div style={grid}>
                <EditableField
                  label="会社名"
                  value={isEditing ? form.company_name : data.company_name || "-"}
                  isEditing={isEditing}
                  onChange={(v) => setForm((p) => ({ ...p, company_name: v }))}
                />

                <EditableField
                  label="会社名（カナ）"
                  value={isEditing ? form.company_name_kana : data.company_name_kana || "-"}
                  isEditing={isEditing}
                  onChange={(v) => setForm((p) => ({ ...p, company_name_kana: v }))}
                />

                                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>業種</div>

                  {!isEditing ? (
                    <div style={readBox}>{data.industry || "-"}</div>
                  ) : (
                    <select
                      value={form.industry ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const needsOther = value === "異業種" || value === "その他";

                        setForm((prev) => ({
                          ...prev,
                          industry: value,
                          industry_other: needsOther ? prev.industry_other : "",
                        }));
                      }}
                      style={inputStyle}
                    >
                      <option value="">選択してください</option>
                      {INDUSTRY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {(!isEditing || form.industry === "異業種" || form.industry === "その他") && (
                  <EditableField
                    label="業種（自由入力）"
                    value={isEditing ? form.industry_other : data.industry_other || "-"}
                    isEditing={isEditing}
                    onChange={(v) => setForm((p) => ({ ...p, industry_other: v }))}
                  />
                )}

                

                <EditableField
                  label="備考"
                  value={isEditing ? form.notes : data.notes || "-"}
                  isEditing={isEditing}
                  onChange={(v) => setForm((p) => ({ ...p, notes: v }))}
                  textarea
                />

                <EditableField
                  label="担当者名"
                  value={isEditing ? form.contact_name : data.contact_name || "-"}
                  isEditing={isEditing}
                  onChange={(v) => setForm((p) => ({ ...p, contact_name: v }))}
                />

                <EditableField
                  label="担当者メール"
                  value={isEditing ? form.contact_email : data.contact_email || "-"}
                  isEditing={isEditing}
                  onChange={(v) => setForm((p) => ({ ...p, contact_email: v }))}
                />

                <EditableField
                  label="担当者電話番号"
                  value={isEditing ? form.contact_phone : data.contact_phone || "-"}
                  isEditing={isEditing}
                  onChange={(v) => setForm((p) => ({ ...p, contact_phone: v }))}
                />

                <ReadOnlyField label="作成日時" value={data.created_at || "-"} />
              </div>
              

                            {/* ✅ NEW: saved multiple contacts */}
              {/* ✅ NEW: saved multiple contacts (editable in 編集モード) */}
<EditableListBox
  title="加盟店担当者（追加）"
  isEditing={isEditing}
  items={((data.merchantContacts as any[]) || []).map((x: any) => ({
    name: String(x?.name ?? x?.contact_name ?? "").trim(),
    email: String(x?.email ?? x?.contact_email ?? "").trim(),
  }))}
  onChange={(next) => {
    setData((prev) => (prev ? { ...prev, merchantContacts: next } : prev));
  }}
/>

<EditableListBox
  title="JMC担当者"
  isEditing={isEditing}
  items={((data.jmcAssignees as any[]) || []).map((x: any) => ({
    name: String(x?.name ?? x?.jmc_name ?? "").trim(),
    email: String(x?.email ?? x?.jmc_email ?? "").trim(),
  }))}
  onChange={(next) => {
    setData((prev) => (prev ? { ...prev, jmcAssignees: next } : prev));
  }}
/>



              <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
                {!isEditing ? (
                  <button style={btnPrimary} onClick={onStartEdit}>
                    編集
                  </button>
                ) : (
                  <>
                    <button style={btnPrimary} onClick={onSave} disabled={saving}>
                      {saving ? "更新中..." : "更新"}
                    </button>
                    <button style={btnSecondary} onClick={onCancelEdit} disabled={saving}>
                      キャンセル
                    </button>
                  </>
                )}

                <button
                  style={{
                    ...btnDanger,
                    opacity: deleting ? 0.7 : 1,
                    cursor: deleting ? "not-allowed" : "pointer",
                  }}
                  onClick={onDelete}
                  disabled={deleting}
                >
                  {deleting ? "削除中..." : "削除"}
                </button>

                <button style={btnSecondary} onClick={() => navigate("/staff/merchants")}>
                  戻る
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </StaffLayout>
  );
}

async function safeReadError(res: Response): Promise<string> {
  try {
    const json = await res.json();
    if (typeof json?.detail === "string") return json.detail;
    return "";
  } catch {
    return "";
  }
}

/** UI Components */


function ListBox({
  title,
  items,
}: {
  title: string;
  items: { name?: string; email?: string }[];
}) {
  const cleaned = Array.isArray(items)
    ? items
        .map((x) => ({
          name: String(x?.name ?? "").trim(),
          email: String(x?.email ?? "").trim(),
        }))
        .filter((x) => x.name || x.email)
    : [];

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontWeight: 800, marginBottom: 8 }}>{title}</div>

      {cleaned.length === 0 ? (
        <div style={{ color: "#475569", fontSize: 13 }}>（登録なし）</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {cleaned.map((x, i) => (
            <div
              key={`${title}-${i}`}
              style={{
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                borderRadius: 12,
                padding: 12,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                  担当者名
                </div>
                <div style={readBox}>{x.name || "-"}</div>
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                  メール
                </div>
                <div style={readBox}>{x.email || "-"}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function EditableListBox({
  title,
  items,
  isEditing,
  onChange,
}: {
  title: string;
  items: { name: string; email: string }[];
  isEditing: boolean;
  onChange: (next: { name: string; email: string }[]) => void;
}) {
  const list = Array.isArray(items) ? items : [];

  const setRow = (idx: number, key: "name" | "email", value: string) => {
    const next = list.map((r, i) => (i === idx ? { ...r, [key]: value } : r));
    onChange(next);
  };

  const addRow = () => {
    onChange([...list, { name: "", email: "" }]);
  };

  const removeRow = (idx: number) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(next.length === 0 ? [{ name: "", email: "" }] : next);
  };

  // show at least 1 row in edit mode
  const viewList =
    isEditing && list.length === 0 ? [{ name: "", email: "" }] : list;

  const cleaned = viewList
    .map((x) => ({ name: String(x?.name ?? "").trim(), email: String(x?.email ?? "").trim() }))
    .filter((x) => (isEditing ? true : x.name || x.email));

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontWeight: 800, marginBottom: 8 }}>{title}</div>

      {cleaned.length === 0 ? (
        <div style={{ color: "#475569", fontSize: 13 }}>（登録なし）</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {cleaned.map((x, i) => (
            <div
              key={`${title}-${i}`}
              style={{
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                borderRadius: 12,
                padding: 12,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 90px",
                gap: 10,
                alignItems: "end",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                  担当者名
                </div>

                {!isEditing ? (
                  <div style={readBox}>{x.name || "-"}</div>
                ) : (
                  <input
                    value={x.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setRow(i, "name", e.target.value)
                    }
                    style={inputStyle}
                    placeholder="例：山田 太郎"
                  />
                )}
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                  メール
                </div>

                {!isEditing ? (
                  <div style={readBox}>{x.email || "-"}</div>
                ) : (
                  <input
                    value={x.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setRow(i, "email", e.target.value)
                    }
                    style={inputStyle}
                    placeholder="例：test@jmc.co.jp"
                  />
                )}
              </div>

              {isEditing ? (
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #fecaca",
                    background: "#fff",
                    color: "#b91c1c",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  削除
                </button>
              ) : (
                <div />
              )}
            </div>
          ))}
        </div>
      )}

      {isEditing && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={addRow}
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #cbd5e1",
              background: "#fff",
              color: "#0f172a",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            ＋ 追加
          </button>
        </div>
      )}
    </div>
  );
}








function InlineMsg({
  kind,
  children,
}: {
  kind: "error" | "success";
  children: React.ReactNode;
}) {
  const isError = kind === "error";
  return (
    <div
      style={{
        marginBottom: 12,
        background: isError ? "#fff1f2" : "#ecfdf5",
        border: isError ? "1px solid #fecdd3" : "1px solid #a7f3d0",
        color: isError ? "#b91c1c" : "#065f46",
        padding: "10px 12px",
        borderRadius: 12,
        fontWeight: 800,
      }}
    >
      {children}
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={labelStyle}>{label}</div>
      <div style={readBox}>{value}</div>
    </div>
  );
}

function EditableField(props: {
  label: string;
  value: string;
  isEditing: boolean;
  onChange: (v: string) => void;
  textarea?: boolean;
}) {
  const { label, value, isEditing, onChange, textarea } = props;

  return (
    <div style={{ minWidth: 0 }}>
      <div style={labelStyle}>{label}</div>

      {!isEditing ? (
        <div style={readBox}>{value || "-"}</div>
      ) : textarea ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          style={textareaStyle}
        />
      ) : (
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
      )}
    </div>
  );
}

/** Styles (UI-only) */

const card: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 16,
  background: "#fff",
};

const accountBox: React.CSSProperties = {
  background: "#f0f9ff",
  border: "1px solid #bae6fd",
  borderRadius: 12,
  padding: 12,
  marginBottom: 16,
};

const grid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
  gap: 14,
  alignItems: "start",
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 6,
  color: "#0f172a",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  height: 44, // keep consistent baseline
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  resize: "vertical",
  boxSizing: "border-box",
};

const readBox: React.CSSProperties = {
  width: "100%",
  height: 44,
  display: "flex",
  alignItems: "center",
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#0f172a",
  fontSize: 14,
  boxSizing: "border-box",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const btnPrimary: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "none",
  background: "#0b1f3b",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
};

const btnDanger: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #fecaca",
  background: "#fff",
  color: "#b91c1c",
  fontWeight: 800,
  cursor: "pointer",
};

const btnSecondary: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 800,
  cursor: "pointer",
};
